import { useState, useEffect, useRef } from 'react';
import { doc, setDoc, getDoc, getFirestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../hooks/useAuth';
import { useLoader } from '../contexts/LoaderContext';
import { logError, logEvent } from '../utils/analytics';
import { SHOP_TYPES } from '../constants/shopTypes';
import { DAYS_OF_WEEK } from '../components/forms/BusinessHoursForm/constant';
import { convertToWebP } from '../utils/imageUpload';
import moment from 'moment';

// Helper to upload image to Firebase Storage
const uploadImageToStorage = async (file, userId, path = 'payment-methods') => {
  try {
    const storage = getStorage();
    const webpBlob = await convertToWebP(file);
    
    const timestamp = Date.now();
    const fileName = `${path.split('/').pop()}-${timestamp}.webp`;
    const storagePath = `${path}/${userId}/${fileName}`;
    
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, webpBlob, {
      contentType: 'image/webp',
    });
    
    const downloadURL = await getDownloadURL(storageRef);
    
    return {
      url: downloadURL,
      path: storagePath
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Helper function to clean up unused images
const cleanupUnusedImages = async (existingImages, newValues) => {
  if (!existingImages) return;
  
  const storage = getStorage();
  const promises = [];
  
  // Get all current image paths from new values
  const currentImagePaths = new Set();
  
  // Find all payment image paths in the new form values
  Object.entries(newValues).forEach(([key, value]) => {
    if (key.startsWith('paymentImages_') && value) {
      Object.values(value).forEach(img => {
        if (img?.path) {
          currentImagePaths.add(img.path);
        }
      });
    }
  });
  
  // Check all existing images and delete those not in current paths
  Object.values(existingImages).forEach(imageData => {
    if (imageData?.path && !currentImagePaths.has(imageData.path)) {
      try {
        const imageRef = ref(storage, imageData.path);
        promises.push(
          deleteObject(imageRef)
            .catch(error => console.error('Error deleting image:', error))
        );
      } catch (error) {
        console.error('Error creating reference for deletion:', error);
      }
    }
  });
  
  await Promise.all(promises);
};

// Function to delete an image from Firebase Storage
export const deleteImageFromStorage = async (imagePath) => {
  if (!imagePath) return;
  
  try {
    const storage = getStorage();
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    return false;
  }
};

export const useBusinessConfig = () => {
  const { user } = useAuth();
  const { showLoader, hideLoader } = useLoader();
  const toastRef = useRef(null);

  const [businessRecord, setBusinessRecord] = useState(null);

  useEffect(() => {
    if (!user) return;

    const fetchBusinessRecord = async () => {
      try {
        const db = getFirestore();
        const businessDocRef = doc(db, 'businesses', user.uid);
        const businessDoc = await getDoc(businessDocRef);
        setBusinessRecord(businessDoc.data());
      } catch (error) {
        logError('useBusinessConfig: fetch business record', error);
      }
    };

    fetchBusinessRecord();
  }, [user]);

  const saveBusinessConfig = async (values) => {
    if (!user?.uid) return false;
    
    try {
      // Create a copy of values to avoid mutating the original
      const valuesToProcess = { ...values };
      
      // Process payment method images
      const paymentImagePromises = [];
      
      // Find all paymentImages fields
      Object.keys(valuesToProcess).forEach(key => {
        if (key.startsWith('paymentImages_') && valuesToProcess[key]) {
          const paymentImages = valuesToProcess[key];
          
          // Process each payment method's image
          Object.entries(paymentImages).forEach(([method, imageData]) => {
            if (imageData?.file && imageData.isNew) {
              // Upload new images
              paymentImagePromises.push(
                uploadImageToStorage(imageData.file, user.uid, 'payment-methods')
                  .then(({ url, path }) => {
                    // Update the image data with the URL and path
                    valuesToProcess[key] = {
                      ...valuesToProcess[key],
                      [method]: {
                        url,
                        path,
                        name: imageData.name || `payment-${method}`
                      }
                    };
                  })
                  .catch(error => {
                    console.error(`Error uploading ${method} image:`, error);
                    // Keep the original image data if upload fails
                    return Promise.resolve();
                  })
              );
            }
          });
        }
      });
      
      // Wait for all image uploads to complete
      await Promise.all(paymentImagePromises);
      
      // Transform values into the desired format
      const transformedData = {
        id: user.uid,
        updatedAt: moment().unix()
      };

      // Get all selected shop types
      const selectedShopTypes = Array.isArray(valuesToProcess.shopType) ? valuesToProcess.shopType : [];

      // Initialize empty array for each category
      selectedShopTypes.forEach(shopType => {
        const category = SHOP_TYPES.find(category =>
          category.items.some(item => item.value === shopType)
        );

        if (category) {
          if (!transformedData[category.label]) {
            transformedData[category.label] = [];
          }

          // Create object with shop type as key
          const serviceData = {
            [shopType]: {
              // Business hours data
              businessHours: {
                days: DAYS_OF_WEEK.map(day => ({
                  openingTime: valuesToProcess[`openingTime_${day.value}_${shopType}`] || '09:00',
                  closingTime: valuesToProcess[`closingTime_${day.value}_${shopType}`] || '17:00',
                  open: valuesToProcess[`open_${day.value}_${shopType}`] || false,
                })),
                hasSpecialHours: valuesToProcess[`hasSpecialHours_${shopType}`] || false,
                specialHoursDescription: valuesToProcess[`specialHoursDescription_${shopType}`] || '',
                hasBreakTime: valuesToProcess[`hasBreakTime_${shopType}`] || false,
                breakStart: valuesToProcess[`breakStart_${shopType}`] || '12:00',
                breakEnd: valuesToProcess[`breakEnd_${shopType}`] || '13:00'
              },

              // Payment methods data
              paymentMethods: valuesToProcess[`paymentMethods_${shopType}`] || [],
              paymentUrls: valuesToProcess[`paymentUrls_${shopType}`] || {},
              paymentImages: valuesToProcess[`paymentImages_${shopType}`] || {},

              // Filter out unwanted fields
              ...Object.keys(values).reduce((acc, key) => {
                // Skip shop config fields
                const shopConfigFields = [
                  'contactEmail', 'contactPhone', 'createdAt', 'directions', 'email',
                  'isActive', 'lastActivity', 'lastLogin', 'markerPosition', 'setupCompleted',
                  'shopName', 'shopType', 'storeAddress', 'storeImages', 'uid', 'updatedAt',
                  'isMainUser', 'assignedFeature'
                ];

                // Skip business hours related fields
                const businessHoursFields = [
                  'openingTime_', 'closingTime_',
                  'hasSpecialHours_', 'specialHoursDescription_',
                  'hasBreakTime_', 'breakStart_', 'breakEnd_'
                ].some(prefix => key.startsWith(prefix + shopType));

                // Skip open_ fields regardless of shop type
                const isOpenField = key.startsWith('open_');

                // Only include subtype-specific fields that aren't business hours fields or open_ fields
                if (key.includes(`_${shopType}`) &&
                  !shopConfigFields.includes(key.split(`_${shopType}`)[0]) &&
                  !businessHoursFields &&
                  !isOpenField &&
                  !key.startsWith('paymentImages_')) { // Skip paymentImages fields
                  acc[key.split(`_${shopType}`)[0]] = valuesToProcess[key];
                }
                return acc;
              }, {})
            }
          };

          // Add to category array
          transformedData[category.label].push(serviceData);
        }
      });

      // Save to Firebase
      const db = getFirestore();
      const businessDocRef = doc(db, 'businesses', user.uid);
      await setDoc(businessDocRef, {
        userUid: user.uid,
        shopType: transformedData
      }, { merge: true });
      
      // Clean up any unused images from previous uploads
      try {
        const existingDoc = await getDoc(businessDocRef);
        const existingData = existingDoc.data();
        
        if (existingData?.shopType) {
          // Find and delete any unused payment method images
          for (const category of Object.values(existingData.shopType)) {
            if (Array.isArray(category)) {
              for (const item of category) {
                for (const shopTypeData of Object.values(item)) {
                  if (shopTypeData.paymentImages) {
                    // Get all current payment images from the form
                    const currentPaymentImages = {};
                    Object.keys(valuesToProcess)
                      .filter(key => key.startsWith('paymentImages_'))
                      .forEach(key => {
                        currentPaymentImages[key] = valuesToProcess[key];
                      });
                    
                    await cleanupUnusedImages(shopTypeData.paymentImages, currentPaymentImages);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error cleaning up old images:', error);
        // Don't fail the entire save operation if cleanup fails
      }
      
      return true;
    } catch (error) {
      console.error('Error preparing business config data:', error);
      return false;
    }
  };

  const handleBusinessSaveClick = async (values, isFinalSubmit) => {
    try {
      showLoader({
        title: 'Saving Business Configuration',
        subText: 'Please wait...'
      });

      const success = await saveBusinessConfig(values);

      if (success && isFinalSubmit) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Business configuration saved successfully!',
          life: 3000
        });
      }

      return success;
    } catch (error) {
      logError('useBusinessConfig: save click', error);
      if (isFinalSubmit) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save business configuration. Please try again.',
          life: 3000
        });
      }
      return false;
    } finally {
      hideLoader();
    }
  };

  return {
    businessRecord,
    handleBusinessSaveClick
  };
};